import Phaser from 'phaser';
import { fetchMe } from '../network/api.js';
import { clearSession, getUser, isApproved } from '../network/session.js';
import { resetOverlay } from '../ui/overlay.js';
import { transitionTo } from '../ui/transition.js';

export class PendingScene extends Phaser.Scene {
  private overlay: HTMLDivElement | null = null;
  private polling = false;

  constructor() {
    super('Pending');
  }

  create(): void {
    resetOverlay();
    // Phaser reuses scene instances; reset transient state on every entry.
    this.polling = false;
    this.add
      .text(this.scale.width / 2, 80, 'Dalmuti Online', {
        fontFamily: 'Georgia, serif',
        fontSize: '46px',
        color: '#8a5e1f',
        fontStyle: 'bold',
        stroke: '#fff7e0',
        strokeThickness: 2,
      })
      .setOrigin(0.5);
    this.mountUI();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unmountUI());
  }

  private goTo(key: string, data?: object): void {
    this.unmountUI();
    transitionTo(this, key, data);
  }

  private mountUI(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const user = getUser();
    const rejected = user?.status === 'rejected';
    const wrap = document.createElement('div');
    wrap.className = 'ui-card';
    wrap.style.cssText =
      'left: 50%; top: 220px; transform: translateX(-50%); width: 380px; text-align:center;';
    wrap.innerHTML = `
      <div style="font-size:40px;">${rejected ? '🚫' : '⏳'}</div>
      <h3 style="margin:8px 0;">${rejected ? '가입이 거절되었습니다' : '승인 대기 중'}</h3>
      <p style="color:#9aa1ad; font-size:13px; line-height:1.6; margin:8px 0 18px;">
        ${
          rejected
            ? '관리자가 이 계정의 가입을 거절했습니다. 문의가 필요하면 관리자에게 연락하세요.'
            : '관리자 승인이 완료되면 게임에 입장할 수 있습니다.<br>승인 후 아래 버튼으로 다시 확인하세요.'
        }
      </p>
      <div style="font-size:12px; color:#f5c869; margin-bottom:16px;">
        ${escapeHtml(user?.email ?? user?.username ?? '')}
      </div>
      <div style="display:flex; flex-direction:column; gap:8px;">
        ${
          rejected
            ? ''
            : '<button id="dm-refresh" style="padding:10px;">승인 상태 다시 확인</button>'
        }
        <button id="dm-logout" style="padding:10px;">로그아웃</button>
      </div>
      <div id="dm-status" style="color:#9aa1ad; font-size:12px; min-height:16px; margin-top:12px;"></div>
    `;
    root.appendChild(wrap);
    this.overlay = wrap;

    wrap
      .querySelector<HTMLButtonElement>('#dm-refresh')
      ?.addEventListener('click', () => void this.recheck());
    wrap
      .querySelector<HTMLButtonElement>('#dm-logout')!
      .addEventListener('click', () => {
        clearSession();
        this.goTo('Login');
      });
  }

  private async recheck(): Promise<void> {
    if (this.polling) return;
    this.polling = true;
    this.setStatus('확인 중…');
    try {
      const user = await fetchMe();
      if (isApproved(user)) {
        this.goTo('Lobby');
        return;
      }
      if (user.status === 'rejected') {
        // Re-render the rejected view.
        this.unmountUI();
        this.mountUI();
        return;
      }
      this.setStatus('아직 승인 대기 중입니다.');
    } catch (err) {
      this.setStatus(`확인 실패: ${(err as Error).message}`);
    } finally {
      this.polling = false;
    }
  }

  private setStatus(text: string): void {
    const el = this.overlay?.querySelector<HTMLDivElement>('#dm-status');
    if (el) el.textContent = text;
  }

  private unmountUI(): void {
    this.overlay?.remove();
    this.overlay = null;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
